import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../../lib/i18n';
import { queryKeys } from '../../../lib/query-client';
import { fetchConnectors } from '../../../lib/connectors';
import { connectorDisplayName, connectorForSlug, skillConnector } from '../../../lib/skill-connector';
import { ConnectorLogo } from '../connectors/ConnectorLogo';
import { Typography } from '../ui';
import styles from './SkillConnectorLine.module.css';

// The connector logo + "Uses your Gmail", under a skill or routine that the genome installed alongside a
// connected app. Renders NOTHING for one that belongs to no connector, or whose connector the user has
// since disconnected — the line is a statement about the user's own setup, so it must not appear
// without one.
//
// Two callers, two ways of naming the same connector: a skill knows its genome `filename` and the slug is
// the folder in it; a routine carries `sourceConnectorSlug` on the entity. Pass whichever you have.
//
// Reads the connectors list from the shared query cache (the same one the connectors screen and the chat
// connect card use), so an open screen picks up a connect or a disconnect without its own fetch.
export function SkillConnectorLine({ filename, slug }: { filename?: string; slug?: string }) {
  const { t } = useTranslation();
  const { data } = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors });

  const connector = slug ? connectorForSlug(slug, data?.connectors) : skillConnector(filename, data?.connectors);
  if (!connector) return null;

  const name = connectorDisplayName(connector);
  return (
    <span className={styles.line}>
      <ConnectorLogo connector={connector} name={name} size={16} radius={4} glyphSize={12} />
      <Typography variant="p3" color="secondary">
        {t('agentSkills.usesConnector', { connector: name })}
      </Typography>
    </span>
  );
}
